import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, CheckCircle2, ArrowRight, Loader2, AlertTriangle, Eye } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_META = {
  hay: { nl: { name: "Hooi-rantsoen calculator", path: "/hooi-rantsoen-calculator", emoji: "🌾", accent: "amber" }, en: { name: "Hay Ration Calculator", path: "/hay-ration-calculator", emoji: "🌾", accent: "amber" } },
  cost: { nl: { name: "Paardenkosten calculator", path: "/paardenkosten-calculator", emoji: "💰", accent: "emerald" }, en: { name: "Horse Cost Calculator", path: "/horse-cost-calculator", emoji: "💰", accent: "emerald" } },
  farrier: { nl: { name: "Hoefsmid-planner", path: "/hoefsmid-planner", emoji: "🦄", accent: "stone" }, en: { name: "Farrier Schedule Calculator", path: "/farrier-schedule-calculator", emoji: "🦄", accent: "stone" } },
  foaling: { nl: { name: "Veulendatum calculator", path: "/veulendatum-calculator", emoji: "🐴", accent: "pink" }, en: { name: "Foaling Date Calculator", path: "/foaling-date-calculator", emoji: "🐴", accent: "pink" } },
  trailer: { nl: { name: "Trailer-gewicht calculator", path: "/trailer-gewicht-calculator", emoji: "🚛", accent: "blue" }, en: { name: "Trailer Weight Calculator", path: "/trailer-weight-calculator", emoji: "🚛", accent: "blue" } },
};

const ACCENTS = {
  amber: { hero: "from-amber-100 via-amber-50 to-white", card: "from-amber-50 to-white", ring: "border-amber-300", btn: "bg-amber-600 hover:bg-amber-700", chip: "bg-amber-100 text-amber-800", text: "text-amber-900" },
  emerald: { hero: "from-emerald-100 via-emerald-50 to-white", card: "from-emerald-50 to-white", ring: "border-emerald-300", btn: "bg-emerald-600 hover:bg-emerald-700", chip: "bg-emerald-100 text-emerald-800", text: "text-emerald-900" },
  stone: { hero: "from-stone-100 via-stone-50 to-white", card: "from-stone-50 to-white", ring: "border-stone-300", btn: "bg-stone-700 hover:bg-stone-900", chip: "bg-stone-100 text-stone-800", text: "text-stone-900" },
  pink: { hero: "from-pink-100 via-pink-50 to-white", card: "from-pink-50 to-white", ring: "border-pink-300", btn: "bg-pink-600 hover:bg-pink-700", chip: "bg-pink-100 text-pink-800", text: "text-pink-900" },
  blue: { hero: "from-blue-100 via-blue-50 to-white", card: "from-blue-50 to-white", ring: "border-blue-300", btn: "bg-blue-600 hover:bg-blue-700", chip: "bg-blue-100 text-blue-800", text: "text-blue-900" },
};

// Pretty-format inputs depending on tool
const formatInputs = (tool, inputs, isNl) => {
  if (!inputs) return [];
  const arr = [];
  if (tool === "hay") {
    arr.push({ k: isNl ? "Gewicht" : "Weight", v: `${inputs.weight} kg` });
    arr.push({ k: isNl ? "Arbeid" : "Work", v: inputs.work });
    arr.push({ k: isNl ? "Hooi" : "Hay", v: `${inputs.hay_kg} kg/${isNl ? "dag" : "day"}` });
    arr.push({ k: isNl ? "Krachtvoer" : "Concentrate", v: `${inputs.concentrate_kg} kg/${isNl ? "dag" : "day"}` });
  } else if (tool === "cost") {
    arr.push({ k: isNl ? "Per maand" : "Per month", v: `€${inputs.monthly}` });
    arr.push({ k: isNl ? "Per jaar" : "Per year", v: `€${inputs.yearly}` });
  } else if (tool === "farrier") {
    arr.push({ k: isNl ? "Discipline" : "Discipline", v: inputs.discipline });
    arr.push({ k: isNl ? "Interval" : "Interval", v: `${inputs.weeks} ${isNl ? "weken" : "weeks"}` });
    arr.push({ k: isNl ? "Beslag" : "Shod", v: inputs.shod });
  } else if (tool === "foaling") {
    arr.push({ k: isNl ? "Dekkingsdatum" : "Cover date", v: inputs.cover_date });
    arr.push({ k: isNl ? "Verwachte datum" : "Due date", v: inputs.expected_date });
    arr.push({ k: isNl ? "Dag in dracht" : "Day of pregnancy", v: `${inputs.days_in}` });
  } else if (tool === "trailer") {
    arr.push({ k: isNl ? "Paarden" : "Horses", v: `${inputs.horses}` });
    arr.push({ k: isNl ? "Combinatie max" : "Combination max", v: `${inputs.combo} kg` });
    arr.push({ k: isNl ? "Rijbewijs" : "License", v: inputs.license });
  }
  return arr;
};

const ToolSharePage = () => {
  const { token } = useParams();
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tool-share/${token}`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e?.response?.status === 404 ? "not_found" : "fetch_error" });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // SEO meta tags (works for users; for FB/Twitter bots we'd need SSR — out of scope)
  useEffect(() => {
    if (!state.data) return;
    const meta = TOOL_META[state.data.tool]?.[isNl ? "nl" : "en"] || TOOL_META[state.data.tool]?.en;
    if (!meta) return;
    const title = `${meta.emoji} ${meta.name} — ${state.data.analysis.slice(0, 50)}…`;
    document.title = title;
    const setMeta = (sel, attr, key, val) => {
      let m = document.querySelector(sel);
      if (!m) { m = document.createElement("meta"); m.setAttribute(attr, key); document.head.appendChild(m); }
      m.setAttribute("content", val);
    };
    const desc = state.data.analysis.slice(0, 160);
    const ogImageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/og/share/${token}.png`;
    setMeta('meta[name="description"]', "name", "description", desc);
    setMeta('meta[property="og:title"]', "property", "og:title", `${meta.emoji} ${meta.name}`);
    setMeta('meta[property="og:description"]', "property", "og:description", desc);
    setMeta('meta[property="og:type"]', "property", "og:type", "article");
    setMeta('meta[property="og:url"]', "property", "og:url", window.location.href);
    setMeta('meta[property="og:image"]', "property", "og:image", ogImageUrl);
    setMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
    setMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", `${meta.emoji} ${meta.name}`);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", desc);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImageUrl);
  }, [state.data, isNl]);

  if (state.loading) {
    return (
      <main className="hac-container py-24 text-center" data-testid="share-loading">
        <Loader2 size={32} className="animate-spin mx-auto text-[#2563EB]" />
        <p className="text-[#64748B] mt-4">{isNl ? "Analyse ophalen..." : "Loading analysis..."}</p>
      </main>
    );
  }

  if (state.error === "not_found") {
    return (
      <main className="hac-container py-24 text-center" data-testid="share-notfound">
        <AlertTriangle size={48} className="mx-auto text-amber-500 mb-3" />
        <h1 className="font-heading text-3xl text-[#0F172A] mb-2">{isNl ? "Deze analyse bestaat niet (meer)" : "This analysis doesn't exist (anymore)"}</h1>
        <p className="text-[#64748B] mb-6">{isNl ? "De gedeelde link is ongeldig. Probeer zelf een nieuwe analyse te maken!" : "The shared link is invalid. Try generating a new analysis yourself!"}</p>
        <Link to="/" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors">{isNl ? "Naar de homepage" : "Back to homepage"} <ArrowRight size={14} /></Link>
      </main>
    );
  }

  if (state.error) {
    return (
      <main className="hac-container py-24 text-center" data-testid="share-error">
        <p className="text-[#64748B]">{isNl ? "Er ging iets mis. Probeer opnieuw." : "Something went wrong. Please try again."}</p>
      </main>
    );
  }

  const { tool, analysis, tips, inputs, views } = state.data;
  const meta = TOOL_META[tool]?.[isNl ? "nl" : "en"] || TOOL_META[tool]?.en;
  if (!meta) return null;
  const C = ACCENTS[meta.accent] || ACCENTS.amber;
  const summary = formatInputs(tool, inputs, isNl);

  return (
    <main data-testid="share-page">
      <section className={`bg-gradient-to-br ${C.hero} border-b border-[#E2E8F0]`}>
        <div className="hac-container py-12 max-w-4xl">
          <div className={`inline-flex items-center gap-2 ${C.chip} px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4`}>
            <Sparkles size={12} /> {isNl ? "Gedeelde AI-analyse" : "Shared AI Analysis"}
            {views > 0 && <span className="opacity-60 normal-case font-normal inline-flex items-center gap-1 ml-1"><Eye size={11} /> {views}</span>}
          </div>
          <h1 className={`font-heading text-3xl md:text-5xl ${C.text} leading-[1.05] mb-3`} data-testid="share-title">{meta.emoji} {meta.name}</h1>
          {summary.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {summary.map((s, i) => (
                <div key={i} className={`bg-white border ${C.ring} rounded-full px-3 py-1.5 text-xs`}>
                  <span className="text-[#64748B]">{s.k}:</span> <span className="font-bold text-[#0F172A]">{s.v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="hac-container py-10 max-w-3xl space-y-6">
        <div className={`bg-gradient-to-br ${C.card} border-2 ${C.ring} rounded-2xl p-6 md:p-8`}>
          <h2 className={`font-bold text-xl ${C.text} mb-4 inline-flex items-center gap-2`}><Sparkles size={18} /> {isNl ? "AI-analyse" : "AI analysis"}</h2>
          <p className={`text-[15px] ${C.text} leading-relaxed whitespace-pre-line`} data-testid="share-analysis">{analysis}</p>
          {tips && tips.length > 0 && (
            <ul className="space-y-2 border-t border-white/50 pt-4 mt-5">
              {tips.map((t, i) => (
                <li key={i} className={`flex items-start gap-2 text-sm ${C.text}`}>
                  <CheckCircle2 size={16} className="shrink-0 mt-0.5 opacity-70" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-[#0F172A] rounded-2xl p-6 md:p-8 text-white text-center">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-3 inline-flex items-center gap-1.5"><Sparkles size={12} /> {isNl ? "Probeer het zelf" : "Try it yourself"}</div>
          <h3 className="font-heading text-2xl md:text-3xl mb-2">{isNl ? `Bereken jouw ${meta.name.toLowerCase()}` : `Calculate your own ${meta.name.toLowerCase()}`}</h3>
          <p className="text-white/70 mb-5 text-sm max-w-md mx-auto">{isNl ? "Gratis · Geen account nodig · AI-analyse in 22 talen" : "Free · No account · AI analysis in 22 languages"}</p>
          <Link to={meta.path} className={`${C.btn} text-white px-7 py-3 rounded-full inline-flex items-center gap-2 transition-colors font-medium`} data-testid="share-cta">
            {isNl ? "Start de calculator" : "Start the calculator"} <ArrowRight size={16} />
          </Link>
        </div>

        <p className="text-center text-xs text-[#94A3B8]">{isNl ? "Aangedreven door " : "Powered by "}<Link to="/" className="text-[#2563EB] hover:underline">HorseAppCompare</Link>{isNl ? " — het globale vergelijkingsplatform voor paarden-apps." : " — the global comparison platform for equestrian apps."} · <Link to={isNl ? "/trending-analyses" : "/trending"} className="text-orange-600 hover:underline">🔥 {isNl ? "Trending analyses" : "Trending analyses"}</Link></p>
      </section>
    </main>
  );
};

export default ToolSharePage;
